import { FC, useContext, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { AdminSupportOptional } from "../../../../../http/patrol/types/supportOptionals";
import { useSupportOptionalsApi } from "../../../../../http/patrol/useSupportOptionals";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import AddSupportOptionalModalDialogButton from "./AddSupportOptionalModalDialogButton";
import SupportOptionalForm from "./SupportOptionalForm";

type Props = {
  // Header actions (search + create) are only published to the shared Header while
  // this tab is the active one, since Tabs keeps every tab's content mounted at once.
  active?: boolean;
};

const SupportOptionals: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const { patrolSystemId } = useParams<{ patrolSystemId: string }>();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "supportOptionals" });
  const aborter = useRef(new AbortController());
  const supportOptionalsApi = useSupportOptionalsApi();
  const { setActions, setTitle } = useSearchContext();

  const [itemToEdit, setItemToEdit] = useState<AdminSupportOptional>();
  const [itemToDelete, setItemToDelete] = useState<AdminSupportOptional>();
  const { accessesAction, accessesDialog } = useEntityAccesses<AdminSupportOptional>("patrol.support_optional");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "supportOptionals");

  const isActiveTab = active ?? true;

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = supportOptionalsApi.useAdminSupportOptionals(query, aborter.current.signal, patrolSystemId);

  const updateVisibility = async (id: string, visibility: VisibilityScopeValue) => {
    try {
      await supportOptionalsApi.updateAdminSupportOptionalVisibility(id, { visibility }, { systemId: patrolSystemId });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.supportOptionals.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.supportOptionals.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <AddSupportOptionalModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<AdminSupportOptional>
        isTall
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.common.description}</th>
            {!patrolSystemId && <th align="left">{i18n.translation.patrolSystems.singular}</th>}
            <th align="left">{i18n.translation.common.onlyFor}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(item) => (
          <>
            <td>
              <Link onClick={(e) => e.stopPropagation()} to={`/admin/items/${item.item.id}`}>
                {item.item.code}
              </Link>
            </td>
            <td>{item.item.description}</td>
            {!patrolSystemId && <td>{item.system?.name}</td>}
            <td>{item.supportExtraDependency?.item.code}</td>
            <td align="center">
              <Visibility value={item.visibility} onChange={(value) => updateVisibility(item.id, value)} />
            </td>
          </>
        )}
        onRowClick={(item) => setItemToEdit(item)}
        rowActions={(item) => [
          getEditAction(i18n, () => setItemToEdit(item)),
          accessesAction(item),
          getDeleteAction(i18n, () => setItemToDelete(item), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}
      {!!itemToEdit && (
        <ModalDialog onClose={() => setItemToEdit(undefined)} title={i18n.translation.supportOptionals.edit}>
          <SupportOptionalForm onClose={() => setItemToEdit(undefined)} defaultValues={itemToEdit} />
        </ModalDialog>
      )}
      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.supportOptionals.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                supportOptionalsApi
                  .deleteAdminSupportOptional(itemToDelete.id, itemToDelete.system?.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.supportOptionals.questions.delete}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

export default SupportOptionals;
